import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTr } from "@/autoTranslate";

const PATTERNS = [
  { icon: "✦", g: "linear-gradient(135deg,#E15A4B,#F4C242)" },
  { icon: "☾", g: "linear-gradient(135deg,#4A9DBB,#4E6E8A)" },
  { icon: "❋", g: "linear-gradient(135deg,#6DA85F,#7A9E7E)" },
  { icon: "◈", g: "linear-gradient(135deg,#D18BC2,#C77B58)" },
  { icon: "☀", g: "linear-gradient(135deg,#F4C242,#E15A4B)" },
  { icon: "❁", g: "linear-gradient(135deg,#B8865C,#956B44)" },
  { icon: "✿", g: "linear-gradient(135deg,#D18BC2,#4A9DBB)" },
  { icon: "♠", g: "linear-gradient(135deg,#8B7F94,#4E6E8A)" },
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function MemoryMatch({ config, onDone, onClose }) {
  const tr = useTr();
  const size = config.size || 4;
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [state, setState] = useState("playing");
  const [seedKey, setSeedKey] = useState(0);

  const deck = useMemo(() => {
    const pairs = Array.from({ length: size }, (_, i) => i);
    const doubled = [...pairs, ...pairs].map((v, idx) => ({
      uid: idx,
      v,
      icon: PATTERNS[v].icon,
      g: PATTERNS[v].g,
    }));
    return shuffle(doubled);
  }, [size, seedKey]);

  const restart = () => {
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setState("playing");
    setSeedKey((k) => k + 1);
  };

  const flip = (uid) => {
    if (flipped.includes(uid) || matched.includes(uid) || state !== "playing")
      return;
    const nf = [...flipped, uid];
    setFlipped(nf);
    if (nf.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = nf.map((u) => deck.find((c) => c.uid === u));
      if (a.v === b.v) {
        setTimeout(() => {
          setMatched((m) => [...m, a.uid, b.uid]);
          setFlipped([]);
        }, 400);
      } else {
        setTimeout(() => setFlipped([]), 800);
      }
    }
  };

  useEffect(() => {
    if (matched.length === size * 2 && state === "playing") {
      setState("win");
      setTimeout(() => onDone && onDone(true), 1500);
    }
  }, [matched, size, state, onDone]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const cols = size === 3 ? 3 : 4;
  const parScore = size === 3 ? 6 : 8;
  const stars = moves <= parScore ? 3 : moves <= parScore + 2 ? 2 : 1;

  return (
    <div className="w-full max-w-[620px]" data-testid="minigame-memory">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Mini oyun")}
          </div>
          <h3 className="mt-1 display-serif italic text-4xl md:text-5xl">
            {tr(config.title)}
          </h3>
        </div>
        <div className="text-right">
          <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">
            {tr("Hamle")}
          </div>
          <div className="display-serif italic text-3xl">{moves}</div>
        </div>
      </div>
      <p className="mt-2 text-sm md:text-base opacity-85">{tr(config.intro)}</p>

      <div className="mt-3 rounded-md border-2 border-[color:var(--ink)] bg-[color:var(--bone-soft)] p-3 text-xs md:text-sm" data-testid="howto-memory">
        <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70">{tr("Nasıl oynanır")}</div>
        <p className="mt-1">{tr("Kartlara ikişer ikişer tıkla ve aynı olan çiftleri eşleştir. Ne kadar az hamlede bitirirsen o kadar iyi.")}</p>
      </div>

      <div
        className="mt-6 grid gap-3"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}
      >
        {deck.map((c) => {
          const isFlipped =
            flipped.includes(c.uid) || matched.includes(c.uid);
          const isMatched = matched.includes(c.uid);
          return (
            <button
              key={c.uid}
              onClick={() => flip(c.uid)}
              data-testid={`memory-card-${c.uid}`}
              className="relative aspect-square"
              style={{ perspective: 800 }}
            >
              <motion.div
                animate={{ rotateY: isFlipped ? 180 : 0 }}
                transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                className="relative w-full h-full"
                style={{ transformStyle: "preserve-3d" }}
              >
                {/* Back */}
                <div
                  className="absolute inset-0 rounded-lg border-2 border-[color:var(--ink)] shadow-[3px_3px_0_0_var(--ink)] flex items-center justify-center"
                  style={{
                    backfaceVisibility: "hidden",
                    background:
                      "repeating-linear-gradient(45deg, #22202e 0 8px, #3a3547 8px 16px)",
                  }}
                >
                  <span className="font-mono-label text-[10px] uppercase tracking-widest text-[color:var(--paper)]">
                    ⌷ ⌷
                  </span>
                </div>
                {/* Front */}
                <div
                  className="absolute inset-0 rounded-lg border-2 border-[color:var(--ink)] shadow-[3px_3px_0_0_var(--ink)] flex items-center justify-center text-4xl md:text-5xl"
                  style={{
                    background: c.g,
                    color: "var(--paper)",
                    backfaceVisibility: "hidden",
                    transform: "rotateY(180deg)",
                    opacity: isMatched ? 0.6 : 1,
                  }}
                >
                  {c.icon}
                  {isMatched && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.15)] rounded-lg">
                      <span className="text-3xl" style={{ color: "var(--paper)" }}>
                        ✓
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-between font-mono-label text-[10px] uppercase tracking-widest opacity-70">
        <span>
          Matched {matched.length / 2} / {size}
        </span>
        <span>Par · {parScore} moves</span>
      </div>

      <AnimatePresence>
        {state === "win" && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-5 p-4 rounded-md border-2 border-[color:var(--ink)] bg-[color:var(--grass)]"
            data-testid="minigame-result-win"
          >
            <div className="flex items-center justify-between">
              <div className="font-mono-label text-[10px] uppercase tracking-widest">
                Reward
              </div>
              <div className="flex gap-0.5 text-2xl">
                {Array.from({ length: 3 }).map((_, i) => (
                  <span
                    key={i}
                    style={{
                      color:
                        i < stars ? "var(--gold)" : "rgba(34,32,46,0.2)",
                    }}
                    data-testid={`memory-star-${i}`}
                  >
                    ★
                  </span>
                ))}
              </div>
            </div>
            <div className="display-serif italic text-2xl">
              {tr(config.reward)}
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={restart}
                className="btn-pill sun"
                data-testid="minigame-retry"
              >
                {tr("Tekrar oyna")}
              </button>
              <button
                onClick={onClose}
                className="btn-pill ghost"
                data-testid="minigame-close"
              >
                {tr("Çık")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
